import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, collection, collectionData, doc, docData, getDoc } from '@angular/fire/firestore';
import { RemoteConfig, fetchAndActivate } from '@angular/fire/remote-config';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { IonicModule, PopoverController } from '@ionic/angular';
import { Observable, Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { MessagesPopoverComponent } from './messages-popover/messages-popover.component';
import { ProfilePopoverComponent } from './profile-popover/profile-popover.component';
import { AppUpdateService } from './services/app-update/app-update.service';
import { AuthService } from './services/auth/auth.service';
import { CommunicationService } from './services/communication/communication.service';
import { DeviceStatusService } from './services/device-status/device-status.service';
import { OrganizationService } from './services/organization/organization.service';

interface Organization {
  name: string;
  shortName: string;
}
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, RouterLink, CommonModule, FormsModule],
})
export class AppComponent implements OnInit, OnDestroy {
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.userIsOnMobile = event.target.innerWidth <= 992;
  }

  auth: Auth = inject(Auth);
  firestore: Firestore = inject(Firestore);
  remoteConfig = inject(RemoteConfig);

  userIsSuperadmin = false;
  userIsOnMobile = window.innerWidth <= 992;
  menuDisabled = false;
  storeMode = environment.storeMode;
  organizationImageUrl: string | null | undefined = undefined;
  appVersion = environment.version;
  
  // Centralized organization selector for superadmins
  organizations$: Observable<Organization[]> | undefined;
  selectedOrganization = '';
  selectedOrganizationImageUrl: string | null = null;

  private offlineDevicesSubscription?: Subscription;
  private organizationDataSubscription?: Subscription;
  private alertsSubscription?: Subscription;
  private logoutSubscription?: Subscription;

  cards: any[] = [
    {
      title: 'Inicio',
      icon: 'home',
      section: 'general',
      url: 'inicio',
      allowedRoles: ['anyone'],
      storeVisible: true
    },
    {
      title: 'Áreas',
      icon: 'bar-chart',
      section: 'general',
      url: 'areas',
      allowedRoles: ['superadmin', 'areas_r', 'areas_p'],
      storeVisible: true
    },
    {
      title: 'Alertas',
      icon: 'warning',
      section: 'general',
      url: 'alertas',
      badge: '0',
      allowedRoles: ['superadmin', 'alerts_r', 'alerts_rw'],
      storeVisible: true
    },
    {
      title: 'Dispositivos',
      icon: 'wifi',
      section: 'general',
      url: 'dispositivos',
      allowedRoles: ['superadmin', 'devices_r', 'devices_rw'],
      storeVisible: true
    },
    {
      title: 'Manuales',
      icon: 'newspaper',
      section: 'general',
      url: 'manuales',
      allowedRoles: ['superadmin', 'manuals_r']
    },
    {
      title: 'Organizaciones',
      icon: 'briefcase',
      section: 'administrative',
      url: 'organizaciones',
      allowedRoles: ['superadmin']
    },
    {
      title: 'Usuarios',
      icon: 'people',
      section: 'administrative',
      url: 'usuarios',
      allowedRoles: ['superadmin', 'users_r', 'users_rw']
    },
    {
      title: 'Facturación',
      icon: 'reader',
      section: 'administrative',
      url: 'facturacion',
      allowedRoles: ['superadmin', 'billing_r']
    },
    {
      title: 'Logs de usuarios',
      icon: 'document-text',
      section: 'administrative',
      url: 'logs-usuarios',
      allowedRoles: ['superadmin', 'user_logs']
    },
    {
      title: 'Soporte',
      icon: 'headset',
      section: 'administrative',
      url: 'soporte',
      allowedRoles: ['support_rw']
    }
  ];

  generalAccessibleCards: any;
  administrativeAccessibleCards: any;

  constructor(private router: Router,
    public authService: AuthService,
    private popoverController: PopoverController,
    private communicationService: CommunicationService,
    private organizationService: OrganizationService,
    private deviceStatusService: DeviceStatusService,
    private appUpdateService: AppUpdateService
  ) { }

  async ngOnInit() {
    this.logoutSubscription = this.authService.logoutStarted$.subscribe(() => {
      this.appUpdateService.stop();
      this.cleanupAuthenticatedResources();
      this.organizationService.clearSelectedOrganization();
      this.selectedOrganization = '';
      this.selectedOrganizationImageUrl = null;
      this.organizations$ = undefined;
    });

    this.auth.onAuthStateChanged(async (data) => {
      if (data) {
        this.appUpdateService.start();
        await this.initializeAuthenticatedResources();
      } else {
        this.appUpdateService.stop();
      }
    });
  }

  private async initializeAuthenticatedResources() {
    await this.initializeMenu();

    if (!this.authService.userIsLoggedIn() || this.authService.logoutInProgress) {
      return;
    }

    await this.loadOrganizationImage();
  }

  get shouldShowMenu(): boolean {
    if (!this.authService.userIsLoggedIn()) {
      return false;
    }
    
    // Don't show menu on password-related pages
    const currentUrl = this.router.url;
    if (currentUrl.includes('/cambiar-contrasena') || currentUrl.includes('/restablecer-contrasena')) {
      return false;
    }
    
    return true;
  }

  async initializeMenu() {
    this.cleanupAuthenticatedResources();

    if (!this.authService.userIsLoggedIn() || this.authService.logoutInProgress) {
      return;
    }

    const accessibleCards = await this.getAccessibleCards();

    if (!this.authService.userIsLoggedIn() || this.authService.logoutInProgress) {
      return;
    }

    this.generalAccessibleCards = accessibleCards.filter(x => x.section == 'general');
    this.administrativeAccessibleCards = accessibleCards.filter(x => x.section == 'administrative');

    await fetchAndActivate(this.remoteConfig);

    if (!this.authService.userIsLoggedIn() || this.authService.logoutInProgress) {
      return;
    }

    this.userIsSuperadmin = this.authService.userHasRole('superadmin');
    
    // Load organizations list for superadmins
    if (this.userIsSuperadmin) {
      this.organizations$ = collectionData(collection(this.firestore, 'organizations')) as Observable<Organization[]>;
    }

    // Offline-devices badge — for regular users (own organization) and superadmins
    // (scoped to the organization they select). The shared service feeds both this
    // menu badge and the Home page tile.
    const devicesCard = this.cards.find(card => card.title === 'Dispositivos');
    this.offlineDevicesSubscription = this.deviceStatusService.offlineCount$.subscribe(offlineCount => {
      if (devicesCard) {
        devicesCard.badge = offlineCount;
      }
    });
    this.deviceStatusService.start();

    // Subscribe to organization data for alerts badge
    if (this.userIsSuperadmin) {
      this.organizationDataSubscription = this.organizationService.selectedOrganization$.subscribe(organizationName => {
        this.subscribeToAlertsCount(organizationName);
      });
    } else {
      this.subscribeToAlertsCount(this.authService.getCurrentUserOrganization().name);
    }

  }

  private subscribeToAlertsCount(organizationName: string) {
    this.alertsSubscription?.unsubscribe();
    this.alertsSubscription = undefined;

    const alertsCard = this.cards.find(card => card.title === 'Alertas');
    if (!organizationName) {
      if (alertsCard) {
        alertsCard.badge = 0;
      }
      return;
    }

    const organizationData = docData(doc(this.firestore, `organizations/${organizationName}`));
    this.alertsSubscription = organizationData.subscribe((data) => {
      if (alertsCard) {
        alertsCard.badge = data?.['unattendedFiredAlertsCount'] || 0;
      }
    });
  }

  private cleanupAuthenticatedResources() {
    this.organizationDataSubscription?.unsubscribe();
    this.organizationDataSubscription = undefined;
    this.alertsSubscription?.unsubscribe();
    this.alertsSubscription = undefined;
    this.offlineDevicesSubscription?.unsubscribe();
    this.offlineDevicesSubscription = undefined;
    this.deviceStatusService.stop();
  }

  async loadOrganizationImage() {
    this.organizationImageUrl = undefined; // Set to undefined to indicate loading
    if (!this.userIsSuperadmin && this.authService.userIsLoggedIn()) {
      const org = this.authService.getCurrentUserOrganization();
      if (org && org.name) {
        const orgDocRef = doc(this.firestore, 'organizations', org.name);
        const orgDocSnap = await getDoc(orgDocRef);
        const orgData = orgDocSnap.data();
        this.organizationImageUrl = orgData && orgData['imageUrl'] ? orgData['imageUrl'] : null;
      } else {
        this.organizationImageUrl = null;
      }
    } else {
      this.organizationImageUrl = null;
    }
  }

  get userIsLoggedIn() {
    return this.authService.userIsLoggedIn();
  }

  get userFullName() {
    return this.authService.getCurrentUserFullName();
  }

  toggleMenu() {
    this.menuDisabled = !this.menuDisabled;
  }

  async openProfilePopover($event: any) {
    const popover = await this.popoverController.create({
      component: ProfilePopoverComponent,
      event: $event
    })
    popover.present();
  }

  async openMessagesPopover($event: any) {
    const popover = await this.popoverController.create({
      component: MessagesPopoverComponent,
      event: $event
    })
    popover.present();
  }

  navigateHome() {
    this.communicationService.pushData({ event: 'navigateHome' });
    this.router.navigate(['inicio']);
  }

  navigateToChangelog() {
    this.router.navigate(['historial-cambios']);
  }

  async getAccessibleCards() {
    const userRoles = this.authService.getCurrentUserRoles();

    return this.cards.filter(card =>
      (!this.storeMode || card.storeVisible) &&
      card.allowedRoles.some((role: any) => role == 'anyone' || userRoles.includes(role)));
  }

  async onOrganizationChange(event: any) {
    this.selectedOrganization = event.detail.value;
    this.organizationService.setSelectedOrganization(this.selectedOrganization);
    await this.loadSelectedOrganizationImage();
  }

  async loadSelectedOrganizationImage() {
    if (this.selectedOrganization) {
      const orgDocRef = doc(this.firestore, 'organizations', this.selectedOrganization);
      const orgDocSnap = await getDoc(orgDocRef);
      const orgData = orgDocSnap.data();
      this.selectedOrganizationImageUrl = orgData && orgData['imageUrl'] ? orgData['imageUrl'] : null;
    } else {
      this.selectedOrganizationImageUrl = null;
    }
  }

  ngOnDestroy() {
    this.appUpdateService.stop();
    this.logoutSubscription?.unsubscribe();
    this.cleanupAuthenticatedResources();
  }
}
